import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { gql, useLazyQuery, useMutation } from "@apollo/client";
import { UPDATE_USER } from "apollo/mutations/Client";
import { listUserHorses } from "apollo/queries/horses";
import { updateUserAttribute } from "aws-amplify/auth";
import { useAuthStore } from "stores/auth/authStore";
import { useProfileStore } from "stores/profile/useProfileStore";
import { useModalStore, Modal as ModalKey } from "stores/modal/useModalStore";
import ModalStore from "components/Modal/ModalStore";
import { formatPhoneNumber, seatSizes } from "utils/index";
import { profileSchema } from "components/Modal/Profile/schemas";

export const useProfileForm = (navigate) => {
  const [isLoading, setIsLoading] = useState(false);
  const [selectedSize, setSelectedSize] = useState([]);
  const [apiSelectedSizes, setApiSelectedSizes] = useState([]);
  const [selectedPlace, setSelectedPlace] = useState({ name: null });
  const [manualAddress, setManualAddress] = useState("");

  // Auth state
  const user = useAuthStore((state) => state.user);
  const fetchFullUserProfile = useAuthStore((state) => state.fetchFullUserProfile);

  // Modal state
  const open = useModalStore(s => s.open);
  const close = useModalStore(s => s.close);
  const current = useModalStore(s => s.current);
  const isOpen = current === ModalKey.Profile;

  // Profile store state
  const imageResult = useProfileStore((state) => state.imageResult);
  const {
    placeAutocomplete,
    countryState,
    aptOrSuite,
    countryCity,
    countryZipcode,
    setPlaceAutocomplete,
    setCountryState,
    setCountryCity,
    setCountryZipcode,
    setAptOrSuite,
  } = useProfileStore();

  const methods = useForm({
    mode: "onBlur",
    resolver: zodResolver(profileSchema),
    defaultValues: {
      country: "United States",
      size: [],
    },
  });

  const [updateUser] = useMutation(gql(UPDATE_USER));
  const [fetchListHorses] = useLazyQuery(gql(listUserHorses));

  useEffect(() => {
    if (countryCity) methods.setValue("city", countryCity, { shouldValidate: true });
    if (countryState) methods.setValue("state", countryState, { shouldValidate: true });
    if (countryZipcode) methods.setValue("zipCode", countryZipcode, { shouldValidate: true });
    if (aptOrSuite) methods.setValue("suiteOrApt", aptOrSuite, { shouldValidate: true });
  }, [countryCity, countryState, countryZipcode, aptOrSuite, methods]);

  // Initialize form data
  useEffect(() => {
    if (user && isOpen) {
      const formattedPhone = formatPhoneNumber(user?.phone);
      methods.reset({
        photo: user.photo || "",
        firstName: user?.firstName || "",
        lastName: user?.lastName || "",
        email: user.email || "",
        phone: formattedPhone || "",
        zipCode: user.zipCode || "",
        streetName: user.streetName || "",
        address: { name: user.address || "" },
        state: user?.state || "",
        city: user.city || "",
        country: user.country || "United States",
        suiteOrApt: user.suiteOrApt || "",
      });

      setCountryCity(user.city || "");
      setCountryState(user.state || "");
      setCountryZipcode(user.zipCode || "");
      setAptOrSuite(user.suiteOrApt || "");
      setSelectedPlace({ name: user.address || null });
    }
  }, [user, isOpen, methods, setAptOrSuite, setCountryCity, setCountryState, setCountryZipcode]);

  // Initialize seat sizes
  useEffect(() => {
    if (!user?.size) return;

    const userDefaultSize = user.size;
    if (Array.isArray(userDefaultSize)) {
      const sizeArray = userDefaultSize
        .map((val) =>
          seatSizes.find((size) => size.value.toString() === val.toString())
        )
        .filter(Boolean);

      setSelectedSize(sizeArray);
      setApiSelectedSizes(userDefaultSize.map((val) => val.toString()));
    } else if (userDefaultSize) {
      const defaultSize = seatSizes.find(
        (size) => size.value.toString() === userDefaultSize.toString()
      );
      if (defaultSize) {
        setSelectedSize([defaultSize]);
        setApiSelectedSizes([defaultSize.value.toString()]);
      }
    }
  }, [user]);

  const handleSubmit = async (data) => {
    try {
      setIsLoading(true);
      const formattedPhoneNumber = data.phone.replace(/\D/g, "");
      const input_user_data = {
        phone: `+${formattedPhoneNumber}`,
        firstName: data?.firstName,
        lastName: data?.lastName,
        address: selectedPlace?.name || manualAddress,
        zipCode: data?.zipCode,
        streetName: data?.streetName,
        city: data?.city,
        state: data?.state,
        suiteOrApt: data?.suiteOrApt,
        country: data?.country,
        photo: imageResult ? imageResult : user?.photo,
        ...(apiSelectedSizes?.length > 0
          ? { size: apiSelectedSizes }
          : { size: [] }),
        saddlePreference: {
          brandName: "",
          condition: "EXCELLENT",
          minFitScore: 1.5,
          newOrUsed: "NEW",
          size: 1.5,
          style: "",
        },
      };

      if (`+${formattedPhoneNumber}` !== user.phone) {
        await updateUserAttribute({
          userAttribute: {
            attributeKey: "phone_number",
            value: `+1${formattedPhoneNumber}`,
          },
        });
      }

      await updateUser({
        variables: { input: input_user_data },
        refetchQueries: ["ClientQuery"],
        awaitRefetchQueries: true,
      });

      await fetchFullUserProfile();

      ModalStore.openSimpleSuccess({
        title: "SUCCESS!",
        subtitle: "User Updated Successfully",
        shouldCloseOnOverlayClick: false,
      });

      close();

      const horsesData = await fetchListHorses({
        fetchPolicy: "network-only",
      });

      if (horsesData?.data?.listUserHorses?.count === 0) {
        navigate("/user/horses");
        setTimeout(() => {
          ModalStore.closeAll();
          ModalStore.openSimpleSuccess({
            fontSize: "40px",
            subTitleFont: "14px",
            title: "Almost There!",
            subtitle: "Step 2 of 2: Add your horse to complete the profile setup.",
            shouldCloseOnOverlayClick: false,
          });
          open(ModalKey.AddHorse);
        }, 500);
      }
    } catch (error) {
      console.error("Error updating user:", error);
      ModalStore.openSimpleError({
        title: "ERROR!",
        subtitle: "Failed to update user. Please try again.",
        shouldCloseOnOverlayClick: false,
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handlePhoneNumberChange = (event) => {
    const input = event.target;
    const rawInput = input.value;
    const cursorPosition = input.selectionStart;
    const rawValue = rawInput.replace(/\D/g, "");
    const value = rawValue.slice(0, 10);
    let formattedValue = "";

    if (value.length > 6) {
      formattedValue = `(${value.slice(0, 3)}) ${value.slice(3, 6)}-${value.slice(6)}`;
    } else if (value.length > 3) {
      formattedValue = `(${value.slice(0, 3)}) ${value.slice(3)}`;
    } else if (value.length > 0) {
      formattedValue = `(${value}`;
    }

    let numericCursorPosition = 0;
    for (let i = 0; i < cursorPosition; i++) {
      if (/\d/.test(rawInput[i])) {
        numericCursorPosition++;
      }
    }

    let adjustedCursorPosition = 0;
    let numericCount = 0;
    for (let i = 0; i < formattedValue.length; i++) {
      if (/\d/.test(formattedValue[i])) {
        numericCount++;
      }
      if (numericCount >= numericCursorPosition) {
        adjustedCursorPosition = i + 1;
        break;
      }
    }

    setTimeout(() => {
      input.value = formattedValue;
      input.selectionStart = input.selectionEnd = Math.min(
        adjustedCursorPosition,
        formattedValue.length
      );
    }, 0);

    methods.setValue("phone", formattedValue);
  };

  const handleSizeChange = (selectedValues) => {
    const cleanValues = Array.isArray(selectedValues)
      ? selectedValues
      : selectedValues
        ? [selectedValues]
        : [];

    setSelectedSize(cleanValues);
    setApiSelectedSizes(
      cleanValues.map((item) =>
        typeof item === "object" ? item.value.toString() : item.toString()
      )
    );
  };

  const hasAddress =
    [selectedPlace?.name, countryCity, countryState, countryZipcode].filter(
      Boolean
    ).length === 4;

  return {
    methods,
    isLoading,
    selectedSize,
    selectedPlace,
    hasAddress,
    placeAutocomplete,
    countryState,
    countryCity,
    countryZipcode,
    aptOrSuite,
    imageResult,
    handleSubmit: methods.handleSubmit(handleSubmit),
    handlePhoneNumberChange,
    handleSizeChange,
    setSelectedPlace,
    setters: {
      setPlaceAutocomplete,
      setCountryState,
      setCountryCity,
      setCountryZipcode,
      setAptOrSuite,
      setManualAddress,
    },
  };
};
